import { useState, useEffect } from "react";
import {
  collection,
  query,
  where,
  onSnapshot,
  type DocumentData,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthProvider";
import { observeUserHasSubscription } from "../services/purchasesService";
import { isSubscriptionGrantingAccess } from "../utils/subscriptionAccess";
import { handleSnapshotError } from "../utils/recoveryErrors";

function isPermissionDeniedError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const code = (error as { code?: unknown }).code;
  return typeof code === "string" && code.toLowerCase().includes("permission-denied");
}

/**
 * Subscription status types matching backend
 */
type SubscriptionStatus =
  | "draft"
  | "pending_initial"
  | "active"
  | "past_due"
  | "canceled"
  | "incomplete"
  | "expired";

/**
 * Subscription document structure (partial)
 */
interface SubscriptionDocument {
  customerId: string;
  planId: string;
  status: SubscriptionStatus;
  currentPeriodEnd: { toDate: () => Date };
  cancelAtPeriodEnd: boolean;
}

/**
 * Premium status details
 */
export interface PremiumStatusDetails {
  subscriptionId: string | null;
  planId: string | null;
  status: SubscriptionStatus | null;
  currentPeriodEnd: Date | null;
  cancelAtPeriodEnd: boolean;
}

/**
 * Return type for usePremiumStatus hook
 */
export interface UsePremiumStatusReturn {
  /** Whether the user has an active premium subscription */
  isPremium: boolean;
  /** Whether premium status is still being determined */
  loading: boolean;
  /** Whether subscription details are still being determined */
  detailsLoading: boolean;
  /** Detailed subscription information */
  details: PremiumStatusDetails;
  /** Error message, if any */
  error: string | null;
}

// Access is determined by isSubscriptionGrantingAccess (status plus a
// past-period check for canceled subscriptions) — see utils/subscriptionAccess.

/**
 * Hook to observe premium subscription status
 *
 * Listens to the subscriptions collection for the linked customerId
 * and determines if the user has an active premium subscription.
 */
export function usePremiumStatus(): UsePremiumStatusReturn {
  const { user, customerId, loading: authLoading } = useAuth();

  const [isPremium, setIsPremium] = useState(false);
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [details, setDetails] = useState<PremiumStatusDetails>({
    subscriptionId: null,
    planId: null,
    status: null,
    currentPeriodEnd: null,
    cancelAtPeriodEnd: false,
  });

  useEffect(() => {
    if (authLoading) {
      queueMicrotask(() => setLoading(true));
      queueMicrotask(() => setDetailsLoading(true));
      return;
    }

    if (!user) {
      queueMicrotask(() => {
        setIsPremium(false);
        setLoading(false);
        setDetailsLoading(false);
        setDetails({
          subscriptionId: null,
          planId: null,
          status: null,
          currentPeriodEnd: null,
          cancelAtPeriodEnd: false,
        });
      });
      return;
    }

    const unsubscribe = observeUserHasSubscription(user.uid, (hasSubscription) => {
      setIsPremium(hasSubscription);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user, authLoading]);

  useEffect(() => {
    if (authLoading) {
      queueMicrotask(() => setDetailsLoading(true));
      return;
    }

    if (!customerId) {
      queueMicrotask(() =>
        setDetails({
          subscriptionId: null,
          planId: null,
          status: null,
          currentPeriodEnd: null,
          cancelAtPeriodEnd: false,
        })
      );
      queueMicrotask(() => setDetailsLoading(false));
      return;
    }

    const subscriptionsRef = collection(db, "subscriptions");
    const subscriptionQuery = query(
      subscriptionsRef,
      where("customerId", "==", customerId)
    );

    queueMicrotask(() => setDetailsLoading(true));
    const unsubscribe = onSnapshot(
      subscriptionQuery,
      (snapshot) => {
        setError(null);

        if (snapshot.empty) {
          setDetails({
            subscriptionId: null,
            planId: null,
            status: null,
            currentPeriodEnd: null,
            cancelAtPeriodEnd: false,
          });
          setDetailsLoading(false);
          return;
        }

        let activeSubscription: (DocumentData & { id: string }) | null = null;

        for (const doc of snapshot.docs) {
          const data = doc.data() as SubscriptionDocument;

          if (isSubscriptionGrantingAccess(data)) {
            if (
              !activeSubscription ||
              (data.status === "active" &&
                (activeSubscription as unknown as SubscriptionDocument)
                  .status !== "active")
            ) {
              activeSubscription = { id: doc.id, ...data };
            }
          }
        }

        if (activeSubscription) {
          const subData = activeSubscription as unknown as SubscriptionDocument;
          setDetails({
            subscriptionId: activeSubscription.id,
            planId: subData.planId,
            status: subData.status,
            currentPeriodEnd: subData.currentPeriodEnd?.toDate() || null,
            cancelAtPeriodEnd: subData.cancelAtPeriodEnd || false,
          });
        } else {
          setDetails({
            subscriptionId: null,
            planId: null,
            status: null,
            currentPeriodEnd: null,
            cancelAtPeriodEnd: false,
          });
        }
        setDetailsLoading(false);
      },
      (err) => {
        const applyErrorState = () => {
          setError(err.message);
          setDetailsLoading(false);
        };
        // Permission-denied is the expected signed-out state — stay silent.
        if (isPermissionDeniedError(err)) {
          applyErrorState();
          return;
        }
        handleSnapshotError("premium_subscriptions", err, applyErrorState);
      }
    );

    return () => unsubscribe();
  }, [customerId, authLoading]);

  return {
    isPremium,
    loading,
    detailsLoading,
    details,
    error,
  };
}
